import React, { useEffect } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import {
  Box,
  Card,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import BackIcon from "@mui/icons-material/Backspace";

type TimeSheetFilterProps = {
  onFilterChange?: (filters: FilterFormData) => void;
};

type FilterFormData = {
  program?: string;
  week?: string;
  worksite?: string;
  status?: string;
  batchId?: string;
};

const defaultValues: FilterFormData = {
  program: "",
  week: "",
  worksite: "",
  status: "",
  batchId: "",
};

const programs = [
  "Summer Youth Employment Program 2024",
  "In-School Youth Program 2024",
  "In-School Youth Program 2023",
  "Youth Leadership Program 2025",
];

const weeks = [
  "Week 01 (06-23-2025 - 06-27-2025)",
  "Week 02 (06-30-2025 - 07-03-2025)",
  "Week 03 (07-07-2025 - 07-11-2025)",
  "Week 04 (07-14-2025 - 07-18-2025)",
  "Week 05 (07-21-2025 - 07-25-2025)",
  "Week 06 (07-28-2025 - 08-01-2025)",
];

const worksites = ["Head Office", "Finance Division", "Marketing Division"];

const statuses = ["Pending", "Batched"];

const batchIds = ["BAT001", "BAT002", "BAT003", "BAT004", "BAT005", "BAT006"];

const TimeSheetFilter: React.FC<TimeSheetFilterProps> = ({
  onFilterChange,
}) => {
  const { control, reset } = useForm<FilterFormData>({
    defaultValues,
  });

  const filters = useWatch({ control });

  useEffect(() => {
    if (onFilterChange) {
      onFilterChange(filters);
    }
  }, [filters, onFilterChange]);

  const handleReset = () => {
    reset();
    onFilterChange?.(defaultValues);
  };

  return (
    <Card variant="outlined" className="m-24 mb-0">
      <form>
        <div className="flex content-between justify-start m-24 flex-wrap gap-16">
          <Box className="w-1/5">
            <FormControl fullWidth size="small">
              <InputLabel>Program</InputLabel>
              <Controller
                name="program"
                control={control}
                render={({ field }) => (
                  <Select {...field} label="Program">
                    {programs.map((prog) => (
                      <MenuItem key={prog} value={prog}>
                        {prog}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
            </FormControl>
          </Box>

          <Box className="w-1/6">
            <FormControl fullWidth size="small">
              <InputLabel>Week</InputLabel>
              <Controller
                name="week"
                control={control}
                render={({ field }) => (
                  <Select {...field} label="Week">
                    {weeks.map((w) => (
                      <MenuItem key={w} value={w}>
                        {w}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
            </FormControl>
          </Box>

          <Box className="w-1/6">
            <FormControl fullWidth size="small">
              <InputLabel>Worksite</InputLabel>
              <Controller
                name="worksite"
                control={control}
                render={({ field }) => (
                  <Select {...field} label="Worksite">
                    {worksites.map((site) => (
                      <MenuItem key={site} value={site}>
                        {site}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
            </FormControl>
          </Box>

          <Box className="w-1/6">
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Controller
                name="status"
                control={control}
                render={({ field }) => (
                  <Select {...field} label="Status">
                    {statuses.map((s) => (
                      <MenuItem key={s} value={s}>
                        {s}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
            </FormControl>
          </Box>

          <Box className="w-1/6">
            <FormControl fullWidth size="small">
              <InputLabel>Batch ID</InputLabel>
              <Controller
                name="batchId"
                control={control}
                render={({ field }) => (
                  <Select {...field} label="Batch ID">
                    {batchIds.map((id) => (
                      <MenuItem key={id} value={id}>
                        {id}
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
            </FormControl>
          </Box>

          <Box className="mt-4">
            <IconButton color="primary" type="submit">
              <SearchIcon />
            </IconButton>
            <IconButton
              color="primary"
              onClick={() => {
                handleReset();
              }}
            >
              <BackIcon />
            </IconButton>
          </Box>
        </div>
      </form>
    </Card>
  );
};

export default TimeSheetFilter;
